// Copyright (c) Microsoft. All rights reserved.

import React, { ComponentType, ReactNode, useEffect } from 'react';
import { useChat } from './libs/hooks/useChat_v2';
import { useAppDispatch, useAppSelector } from './redux/app/hooks';
import { RootState } from './redux/app/store';
import { AuthHelper } from './libs/auth/AuthHelper';
import { usePlugin } from './libs/hooks/usePlugin';
import { setUsers } from './redux/features/users/usersSlice';

import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ChatView } from './components/views/ChatView';
import { AlertType } from './libs/models/AlertType';
import { removeAlertToast, setAppConnectState, showAlertToast } from './redux/features/app/appSlice';

import { AlertModal } from './components/AlertModal';
import { AlertToast } from './components/AlertToast';
import { PageNotFound } from './pages/PageNotFound';
import PluginPromptHome from './pages/studio/PluginPromptHome';
import PluginPromptWindow from './pages/studio/PluginPromptWindow';
import PluginCreate from './pages/studio/plugin/PluginCreate';
import AionuCreate from './pages/studio/plugin/AIONUCreate';
import PromptCreate from './pages/studio/prompt/PromptCreate';
import DashboardLayout from './pages/dashboard/DashboardLayout';
import { MSAuthCallback } from './libs/services/MSAuthCallback';
import './styles/global.css';
import './styles/tool.css';
import './styles/chat.css';
import './styles/dashboard.css';
import './styles/scss/chat.scss';
import './styles/stackoverflow-dark.css';
import { AlertProvider } from './context/AlertContext';
import { IPluginPromptData } from './libs/models/studio/PluginPromptList';
import { AppConnectState } from './libs/models/app/AppState';
import { setLocalMcpTools, setSkills } from './redux/features/app/toolSlice';
import { IMCPTool } from './libs/models/app/mcp/Tool';
import { useApp } from './libs/hooks/useApp';

interface AppProviderProps {
  contexts: Array<ComponentType<any>>;
  children: ReactNode;
}

const AppProvider: React.FC<AppProviderProps> = ({ contexts, children }) =>
  contexts.reduce((prev, Context) => React.createElement(Context, null, prev), children);

declare global {
  interface Window {
    setPlugin: (pluginData: IPluginPromptData) => void;
    setPrompt: (pluginData: IPluginPromptData) => void;
  }
}

const App = () => {
  const dispatch = useAppDispatch();
  const { appConnectState, isMaintenance } = useAppSelector((state: RootState) => state.app);

  // const isAuthenticated = useIsAuthenticated();
  // 인증 통과
  const isAuthenticated = true;

  const handleUserAuth = async () => {
    try {
      const result = await AuthHelper.getLoggedInUser();
      dispatch(setUsers(result));
      dispatch(setAppConnectState(AppConnectState.LoadingChats));
    } catch (e) {
      dispatch(setAppConnectState(AppConnectState.ErrorLoadingUserInfo));
      if (self != top) dispatch(showAlertToast({
        altMessage: `오류가 발생하였습니다. 키보드 Ctrl+Shift+Del를 한번에 누르고 '쿠키 및 기타 사이트 데이터'(*지난 7일) 삭제 후 재접속해주세요.`,
        altType: AlertType.Info,
      }))
      else alert('로그인을 실패했어요. Works 로그인 후 다시 접속해 주세요.');
    }
  };

  useEffect(() => {
    if (appConnectState === AppConnectState.SettingUserInfo) {
      void handleUserAuth();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, appConnectState, isMaintenance]);

  const { toastMessage } = useAppSelector((state: RootState) => state.app);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (toastMessage.length > 0) {
      timer = setTimeout(() => {
        dispatch(removeAlertToast());
      }, 3000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [toastMessage, dispatch]);

  // const content = <Chat classes={classes} appState={appState} />;
  const router = createBrowserRouter([
    {
      path: '/',
      errorElement: <PageNotFound />,
      element: <Chat />,
    },
    {
      path: '/chat/:chatRoomId',
      element: <Chat />,
    },
    {
      path: '/makers',
      element: <PluginPromptWindow />,
      children: [
        {
          path: 'agent',
          element: <PluginPromptHome menuType="plugin" />,
        },
        {
          path: 'template', // 프롬프트의 경우, url만 template 으로 받고, 코드 상에서는 prompt로 쓴다.
          element: <PluginPromptHome menuType="prompt" />,
        },
        {
          path: 'agent/pluginCreate',
          element: <PluginCreate />,
        },
        {
          path: 'agent/AionuCreate',
          element: process.env.REACT_APP_AIONU === 'TRUE' ? <AionuCreate /> : <Navigate to="/makers/agent" replace />,
        },
        {
          path: 'prompt/promptCreate',
          element: <PromptCreate />,
        },
      ],
    },
    {
      path: '/dashboard',
      element: <DashboardLayout />,
    },
    {
      path: '/auth/callback',
      element: <MSAuthCallback />,
    },
    {
      path: '/error',
      errorElement: <PageNotFound />,
      element: <PageNotFound />,
    },
  ]);

  return (
    <>
      <AppProvider contexts={[AlertProvider]}>
        {/* {content} */}
        <AlertModal />
        <AlertToast />
        <RouterProvider router={router} />
      </AppProvider>
    </>
  );
};

const Chat = () => {
  const dispatch = useDispatch();
  const currentAppState = useAppSelector((state: RootState) => state.app.appConnectState);
  const chat = useChat();
  const plugin = usePlugin();
  const app = useApp();
  const { chatRoomId } = useParams();

  useEffect(() => {
    if (currentAppState === AppConnectState.LoadingChats) {
      void Promise.all([
        chat
          .getAllModel()
          .then(() => { })
          .catch((err) => {
            console.error(err);
          }),
        plugin
          .getAllPlugin()
          .then(() => { })
          .catch((err) => {
            console.error(err);
          }),
        // Load all chats from memory / 초기 n개
        window.electron?.isElectron ?
          requestMCPToolList().then((r: Record<string, IMCPTool[]>) => {
            dispatch(setLocalMcpTools(r));
          }).catch((err) => {
            console.error(err);
          })
          : ''
        ,
        // 스킬 목록 초기 로드 (MCP 도구와 동일하게 redux 로 관리)
        window.electron?.isElectron && window.electron.skillList ?
          window.electron.skillList().then((list) => {
            dispatch(setSkills(list ?? []));
          }).catch((err) => {
            console.error(err);
          })
          : ''
        ,
        app.getUserConnectors()
          .then(()=>{}).catch((err) => {
            console.error(err);
          })
        ,
        chat
          .loadChats(chatRoomId)
          .then(() => {
            dispatch(setAppConnectState(AppConnectState.Chat));
          })
          .catch(() => {
            dispatch(
              showAlertToast({
                altMessage: String('채팅방에 권한이 없어요. 다른 채팅방을 선택해주세요.'),
                altType: AlertType.Error,
              }),
            );
            dispatch(setAppConnectState(AppConnectState.ErrorLoadingChats));
          }),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAppState]);

  if (window.electron?.isElectron) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    window.electron.completeInitialize((data: Record<string, IMCPTool[]>) => {
      dispatch(setLocalMcpTools(data));
    });
  }


  const requestMCPToolList = async () => {
    const response = await (window.electron.requestMCPToolList as () => Promise<Record<string, IMCPTool[]>>)();
    return response;
  }
  return (
    // <div className={classes.container}>

    <>
      {/* {appState === AppState.LoadingChats && <Loading text="로딩중입니다. 잠시만 기다려주세요." />} */}
      {currentAppState === AppConnectState.Chat && <ChatView />}
    </>
  );
};

export default App;
